import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { buildApiUrl } from '../config/api';
import { schooldetails } from '../redux/schooldetails';
import { isLiberiaSchool } from '../utils/currencyFormatter';

const SMS_RETURN_QUERY = 'sis_sms_tokens';

/**
 * Package B (fixed plan): SMS unit wallet — GHS 0.06/unit, 70 characters = 1 unit.
 */
export default function SmsTokenWallet() {
    const dispatch = useDispatch();
    const admin = useSelector((state) => state.admin) || {};
    const schooldetails_ = useSelector((state) => state.schooldetails) || [];
    const school = schooldetails_[0];

    const [wallet, setWallet] = React.useState(null);
    const [amountGhs, setAmountGhs] = React.useState('10');
    const [loading, setLoading] = React.useState(false);
    const [paying, setPaying] = React.useState(false);
    const [snack, setSnack] = React.useState(null);
    const returnHandled = React.useRef(false);

    const packageB =
        !isLiberiaSchool(school, admin) &&
        (wallet?.packageB === true ||
        String(school?.subscriptionPackage || '').toLowerCase() === 'fixed');

    const refreshWallet = React.useCallback(async () => {
        if (!admin.schoolID) return;
        setLoading(true);
        try {
            const res = await axios.post(buildApiUrl('/api/schools/sms-wallet'), {
                schoolID: admin.schoolID
            });
            setWallet(res.data);
        } catch (err) {
            setSnack(err.response?.data?.message || err.message || 'Could not load SMS wallet');
        } finally {
            setLoading(false);
        }
    }, [admin.schoolID]);

    React.useEffect(() => {
        if (!admin.schoolID) return;
        refreshWallet();
    }, [admin.schoolID, refreshWallet, school?.smsTokenBalance]);

    React.useEffect(() => {
        if (!packageB || returnHandled.current) return;
        const q = new URLSearchParams(window.location.search);
        if (q.get(SMS_RETURN_QUERY) !== '1') return;
        const reference = q.get('reference') || q.get('trxref');
        if (!reference) return;
        returnHandled.current = true;
        axios
            .post(buildApiUrl('/api/schools/sms-tokens-verify'), { reference })
            .then((res) => {
                if (res.data && res.data.school) {
                    dispatch(schooldetails({ data: [res.data.school] }));
                }
                if (res.data && res.data.wallet) {
                    setWallet(res.data.wallet);
                }
                const path = window.location.pathname || '/';
                const hash = window.location.hash || '#/';
                window.history.replaceState({}, document.title, path + hash);
                setSnack(
                    res.data.unitsCredited
                        ? `Credited ${res.data.unitsCredited} SMS units. Thank you.`
                        : 'SMS units purchase recorded.'
                );
            })
            .catch((err) => {
                returnHandled.current = false;
                setSnack(err.response?.data?.message || err.message || 'Could not verify payment');
            });
    }, [packageB, dispatch]);

    React.useEffect(() => {
        if (!snack) return undefined;
        const t = setTimeout(() => setSnack(null), 7000);
        return () => clearTimeout(t);
    }, [snack]);

    const price = wallet?.pricePerUnitGhs ?? 0.06;
    const chars = wallet?.charsPerUnit ?? 70;
    const balance = wallet?.balance ?? school?.smsTokenBalance ?? 0;
    const amountNum = parseFloat(amountGhs);
    const unitsPreview =
        Number.isFinite(amountNum) && amountNum > 0 ? Math.floor(amountNum / price) : 0;

    const handlePurchase = async () => {
        if (!admin.schoolID) {
            setSnack('Missing school session');
            return;
        }
        if (!Number.isFinite(amountNum) || amountNum < 1) {
            setSnack('Enter at least GHS 1.00');
            return;
        }
        setPaying(true);
        setSnack(null);
        const callbackUrl = `${window.location.origin}${window.location.pathname || '/'}?${SMS_RETURN_QUERY}=1`;
        try {
            const res = await axios.post(buildApiUrl('/api/schools/sms-tokens-initialize'), {
                schoolID: admin.schoolID,
                amountGhs: amountNum,
                callbackUrl
            });
            if (res.data && res.data.authorization_url) {
                window.location.href = res.data.authorization_url;
                return;
            }
            setSnack('Could not start payment');
        } catch (err) {
            setSnack(err.response?.data?.message || err.message || 'Payment start failed');
        } finally {
            setPaying(false);
        }
    };

    if (!packageB) {
        return null;
    }

    return (
        <Box
            sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                border: '1px solid #dbeafe',
                bgcolor: '#f0f9ff'
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0c4a6e' }}>
                SMS units (Silver plan)
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: '#0369a1' }}>
                Bulk parent SMS uses prepaid units. <strong>GHS {price.toFixed(2)}</strong> per unit;{' '}
                <strong>{chars} characters</strong> = 1 unit (per recipient message, including the parent name).
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0c4a6e', m: 0 }}>
                    {loading ? '…' : balance} <span style={{ fontSize: 14, fontWeight: 600 }}>units left</span>
                </Typography>
                <Button size="small" onClick={refreshWallet} disabled={loading}>
                    Refresh
                </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Buy more units (Paystack)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>
                <TextField
                    size="small"
                    label="Amount (GHS)"
                    type="number"
                    inputProps={{ min: 1, step: 0.01 }}
                    value={amountGhs}
                    onChange={(e) => setAmountGhs(e.target.value)}
                    sx={{ width: 140, bgcolor: 'white' }}
                />
                <Typography variant="body2" color="text.secondary">
                    ≈ {unitsPreview} units
                </Typography>
                <Button variant="contained" disabled={paying || unitsPreview < 1} onClick={handlePurchase}>
                    {paying ? 'Please wait…' : 'Pay with Paystack'}
                </Button>
            </Box>

            {snack ? (
                <Typography variant="body2" sx={{ mt: 2, color: snack.includes('Could') ? 'error.main' : 'success.main' }}>
                    {snack}
                </Typography>
            ) : null}
        </Box>
    );
}
